import { FormEvent, useState } from 'react';
import { CalendarClock, Loader2, Trash2 } from 'lucide-react';
import { useBrand } from '../../contexts/BrandContext';
import { useToast } from '../../contexts/ToastContext';
import { useAddEmailContextNote, useDeleteEmailContextNote, useEmailContextNotes } from '../../queries/emailContextNotes';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';

const inputClass = 'mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/**
 * Notes for the next scheduled tips email (Wed/Sat/Sun 8:00am, n8n Best Tips Email Scheduler).
 * The scheduler adds the day's feature races and any hot form itself; these are the things only
 * a person knows ("new site is live", "Caulfield tips are free today"). Each note is used once.
 */
export function ScheduledEmailNotesPanel() {
  const { brand } = useBrand();
  const { success, error } = useToast();
  const { data: notes = [], isLoading } = useEmailContextNotes();
  const add = useAddEmailContextNote();
  const remove = useDeleteEmailContextNote();
  const [note, setNote] = useState('');
  const [bothBrands, setBothBrands] = useState(true);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!note.trim()) return;
    add.mutate(
      { note: note.trim(), bothBrands },
      {
        onSuccess: () => {
          setNote('');
          success('Note saved for the next scheduled email.');
        },
        onError: () => error('Could not save the note. Please try again.'),
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarClock className="h-5 w-5" />
          Notes for the next scheduled email
        </CardTitle>
        <CardDescription>
          Emails are drafted automatically on Wednesday, Saturday and Sunday at 8:00am with the day&apos;s feature races and any hot form. Add anything
          else you want mentioned; each note is used once.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : notes.length === 0 ? (
          <p className="text-sm text-muted-foreground">No notes waiting.</p>
        ) : (
          <ul className="space-y-2" aria-label="Notes waiting">
            {notes.map((n) => (
              <li key={n.id} className="flex items-start justify-between gap-3 rounded-md border border-border px-3 py-2 text-sm">
                <div>
                  <p>{n.note}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{n.brand_key ? `${brand.name} only` : 'Both brands'}</p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={`Remove note: ${n.note}`}
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(n.id, { onError: () => error('Could not remove the note.') })}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}

        <form onSubmit={submit} className="space-y-2">
          <label htmlFor="scheduled-email-note" className="text-sm font-medium">
            Add a note
          </label>
          <textarea
            id="scheduled-email-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            maxLength={1000}
            placeholder="e.g. Caulfield tips are free today"
            className={`${inputClass} resize-none`}
          />
          <div className="flex items-center justify-between gap-2">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={bothBrands} onChange={(e) => setBothBrands(e.target.checked)} />
              Both brands (untick for {brand.name} only)
            </label>
            <Button type="submit" disabled={add.isPending || !note.trim()}>
              {add.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Save note
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
